// MIT License - Copyright (c) 2026 Doug McNall

/**
 * Crew Backstories
 * 
 * Backstory templates for crew members, revealed as relationships deepen
 */

export const backstoryTemplates = {
  // Pilot backstories
  pilot_1: {
    role: 'pilot',
    title: 'Former Racing Champion',
    story: 'Won the Kessel Run Championship three years running before a scandal forced retirement. Still has the reflexes.',
    unlockThreshold: 60,
    traits: ['brave', 'competitive']
  },
  pilot_2: {
    role: 'pilot',
    title: 'Military Deserter',
    story: 'Fled the Imperial Navy after refusing an order to fire on civilians. Excellent pilot, haunted past.',
    unlockThreshold: 70,
    traits: ['cautious', 'principled']
  },
  pilot_3: {
    role: 'pilot',
    title: 'Smuggler\'s Apprentice',
    story: 'Learned to fly running contraband through asteroid fields. Knows every trick in the book.',
    unlockThreshold: 65,
    traits: ['cunning', 'resourceful']
  },
  pilot_4: {
    role: 'pilot',
    title: 'Test Pilot Survivor',
    story: 'Only survivor of an experimental warp drive test. Has seen things others can\'t imagine.',
    unlockThreshold: 80,
    traits: ['brave', 'haunted']
  },

  // Engineer backstories
  engineer_1: {
    role: 'engineer',
    title: 'Shipyard Prodigy',
    story: 'Built first starship engine at age 16. Expelled from academy for "unauthorized modifications."',
    unlockThreshold: 60,
    traits: ['technical', 'rebellious']
  },
  engineer_2: {
    role: 'engineer',
    title: 'Salvage Expert',
    story: 'Spent years stripping derelict ships in the outer rim. Can fix anything with spare parts.',
    unlockThreshold: 65,
    traits: ['resourceful', 'practical']
  },
  engineer_3: {
    role: 'engineer',
    title: 'Corporate Whistleblower',
    story: 'Exposed faulty reactor designs that would have killed thousands. Now blacklisted by major corporations.',
    unlockThreshold: 75,
    traits: ['principled', 'technical']
  },
  engineer_4: {
    role: 'engineer',
    title: 'AI Researcher',
    story: 'Worked on classified AI projects before the program was shut down. Knows secrets about ship computers.',
    unlockThreshold: 80,
    traits: ['technical', 'secretive']
  },

  // Gunner backstories
  gunner_1: {
    role: 'gunner',
    title: 'War Veteran',
    story: 'Fought in the Border Wars. Decorated marksman with 47 confirmed kills. Doesn\'t talk about it.',
    unlockThreshold: 70,
    traits: ['brave', 'haunted']
  },
  gunner_2: {
    role: 'gunner',
    title: 'Bounty Hunter',
    story: 'Tracked down fugitives across three sectors. Retired after a job went wrong.',
    unlockThreshold: 65,
    traits: ['cunning', 'ruthless']
  },
  gunner_3: {
    role: 'gunner',
    title: 'Security Chief',
    story: 'Protected VIPs on luxury liners. Left after failing to stop an assassination.',
    unlockThreshold: 75,
    traits: ['cautious', 'protective']
  },
  gunner_4: {
    role: 'gunner',
    title: 'Pirate Defector',
    story: 'Served with the Red Corsairs before turning on them. Still has a price on their head.',
    unlockThreshold: 80,
    traits: ['brave', 'rebellious']
  },

  // Medic backstories
  medic_1: {
    role: 'medic',
    title: 'Plague Doctor',
    story: 'Treated victims of the Centauri Plague. Saved hundreds but lost family to the disease.',
    unlockThreshold: 70,
    traits: ['compassionate', 'haunted']
  },
  medic_2: {
    role: 'medic',
    title: 'Combat Surgeon',
    story: 'Operated in field hospitals during the war. Can perform surgery in zero-g with one hand.',
    unlockThreshold: 65,
    traits: ['brave', 'practical']
  },
  medic_3: {
    role: 'medic',
    title: 'Xenobiologist',
    story: 'Studied alien physiology on frontier worlds. Knows how to treat species most doctors have never seen.',
    unlockThreshold: 75,
    traits: ['curious', 'technical']
  },
  medic_4: {
    role: 'medic',
    title: 'Underground Healer',
    story: 'Ran illegal clinic treating refugees and criminals. Wanted by authorities in three systems.',
    unlockThreshold: 80,
    traits: ['compassionate', 'rebellious']
  },

  // Scientist backstories
  scientist_1: {
    role: 'scientist',
    title: 'Disgraced Researcher',
    story: 'Published controversial findings about warp space. Blacklisted by the scientific community.',
    unlockThreshold: 70,
    traits: ['technical', 'rebellious']
  },
  scientist_2: {
    role: 'scientist',
    title: 'First Contact Specialist',
    story: 'Made first contact with three alien species. Knows protocols others don\'t.',
    unlockThreshold: 65,
    traits: ['curious', 'diplomatic']
  },
  scientist_3: {
    role: 'scientist',
    title: 'Quantum Physicist',
    story: 'Worked on classified projects involving alternate dimensions. Saw things that shouldn\'t exist.',
    unlockThreshold: 80,
    traits: ['technical', 'haunted']
  },
  scientist_4: {
    role: 'scientist',
    title: 'Archaeological Explorer',
    story: 'Discovered ancient alien ruins on dead worlds. Believes precursor civilizations left warnings.',
    unlockThreshold: 75,
    traits: ['curious', 'obsessed']
  },

  // Diplomat backstories
  diplomat_1: {
    role: 'diplomat',
    title: 'Peace Negotiator',
    story: 'Brokered the Treaty of Andromeda, ending a 20-year war. Enemies on both sides want revenge.',
    unlockThreshold: 70,
    traits: ['diplomatic', 'cautious']
  },
  diplomat_2: {
    role: 'diplomat',
    title: 'Corporate Liaison',
    story: 'Negotiated billion-credit deals between megacorps. Left after discovering corruption at the highest levels.',
    unlockThreshold: 65,
    traits: ['cunning', 'principled']
  },
  diplomat_3: {
    role: 'diplomat',
    title: 'Cultural Attaché',
    story: 'Lived among alien cultures for years. Speaks 12 languages and understands customs others find baffling.',
    unlockThreshold: 75,
    traits: ['curious', 'diplomatic']
  },
  diplomat_4: {
    role: 'diplomat',
    title: 'Spy Master',
    story: 'Ran intelligence operations under diplomatic cover. Knows secrets that could topple governments.',
    unlockThreshold: 80,
    traits: ['cunning', 'secretive']
  }
};

/**
 * Assign a backstory to a crew member based on role and personality
 */
export function assignBackstory(crewMember) {
  const roleBackstories = Object.entries(backstoryTemplates)
    .filter(([_, template]) => template.role === crewMember.role)
    .map(([id, template]) => ({ id, ...template }));

  if (roleBackstories.length === 0) {
    return null;
  }

  // Try to match personality traits
  const personalityTraits = crewMember.personality 
    ? Object.keys(crewMember.personality) 
    : [];
  const matchingBackstories = roleBackstories.filter(backstory =>
    backstory.traits.some(trait => personalityTraits.includes(trait))
  );

  // Use matching backstory if found, otherwise random
  const candidates = matchingBackstories.length > 0 ? matchingBackstories : roleBackstories;
  const selected = candidates[Math.floor(Math.random() * candidates.length)];

  return {
    id: selected.id,
    title: selected.title,
    story: selected.story,
    unlockThreshold: selected.unlockThreshold,
    revealed: false
  };
}

/**
 * Check if a backstory should be unlocked based on relationship
 */
export function checkBackstoryUnlock(crewMember, relationshipScore) {
  if (!crewMember.backstory || crewMember.backstory.revealed) {
    return false;
  }

  return relationshipScore >= crewMember.backstory.unlockThreshold;
}

/**
 * Reveal a crew member's backstory
 */
export function revealBackstory(crewMember) {
  if (!crewMember.backstory) {
    return null;
  }

  crewMember.backstory.revealed = true;
  return crewMember.backstory;
}

export default backstoryTemplates;
